import { defineStore } from 'pinia'
import { useAgeVerificationStore } from './age-verification'
import { useCustomerStore } from 'src/stores/customer'
import { useAuthStore } from 'src/stores/auth'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'

export const useHomePageStore = defineStore('homePage', {
  state: () => ({
    loading: true,
    homePageData: null,
    products: {},
    hideComponent: {},
  }),
  getters: {
    getProductsByCollectionId: (state) => (collectionId) =>
      state.products[collectionId] || false,
  },
  actions: {
    /**
     * Fetches the home page template data from the API
     * @returns {Promise<void>} Returns nothing, but updates the store state with:
     * - homePageData: The home page template data from the API
     * - hideComponent: Reset to empty object
     * - loading: Set to false when complete
     */
    async getHomePageTemplate() {
      const ageVerificationStore = useAgeVerificationStore()
      const persistedStore = usePersistedStore()
      this.loading = true

      try {
        // Get required params from vuex store once
        const locationId = persistedStore.currentLocationId
        const filterAgeRestricted = ageVerificationStore.showAgeVerificationBtn

        // Make API request with params
        const { data: { data: templateData } = {} } =
          await this.$axiosInstance.get('/PageTemplate', {
            params: {
              locationID: locationId,
              filterAgeRestrictedItem: filterAgeRestricted,
            },
          })

        // Update store if data exists
        if (templateData) {
          this.hideComponent = {}
          this.homePageData = templateData
        }
      } catch (error) {
        console.error('Error fetching home page template:', error)
        throw error
      } finally {
        this.loading = false
      }
    },
    /**
     * Fetches products for a specific collection from the API
     * @param {Object} collection - The collection object containing id and other properties
     * @returns {Promise<Object|null>} Returns the API response data or null if error/invalid collection
     */
    async getProductsByCollection(collection, isPreview = false) {
      if (!collection?.id) return null

      const ageVerificationStore = useAgeVerificationStore(),
        customerStore = useCustomerStore(),
        authStore = useAuthStore(),
        persistedStore = usePersistedStore(),
        commonStore = useCommonStore()

      // Build base params object
      const params = {
        collectionId: collection.id,
        locationId: persistedStore.currentLocationId,
        zipCode: persistedStore.locationOrShippingZip,
        productsBy: 'CL',
        Page: 1,
        PageSize: 15,
        global: true,
        ...commonStore.currentDeliveryModes,
      }

      // Add age restriction param if needed
      const shouldSkipAgeRestricted =
        ageVerificationStore.skipBrowsingAgeRestrictedProd &&
        ((!authStore.isLoggedIn &&
          !persistedStore.eaivPersistedVerificationSuccess) ||
          (authStore.isLoggedIn && !customerStore.customer?.isVerifiedOnline))

      if (shouldSkipAgeRestricted) {
        params.skipBrowsingAgeRestrictedProd = true
      }

      try {
        const response = await this.$axiosInstance.get('/Product', { params })
        const items = response?.data?.data?.items || []
        if (isPreview === false) {
          this.products = {
            ...this.products,
            [collection.id]: items,
          }
        }

        return response?.data?.data
      } catch (error) {
        this.products = {
          ...this.products,
          [collection.id]: [],
        }
        return null
      }
    },
    /**
     * Updates visibility state of a component in the hideComponent object
     * @param {Object} params - Object containing component id and hide state
     * @param {string} params.compId - ID of the component to update
     * @param {boolean} params.hide - Whether to hide the component
     */
    updateHideComponent({ compId, hide }) {
      this.hideComponent = {
        ...this.hideComponent,
        [compId]: hide,
      }
    },
  },
})
